// Block layer for the scheduler-composer — expansion back into the Draft.
//
// Companion to programBlocks.ts (model + derivation). These are the pure
// `(draft, block, …) => Draft` operations the composer binds to ＋ AGGIUNGI /
// Aggiorna / ⧉ Clona / ✕ Rimuovi. Expansion follows redesign §2.2: for every
// placement day, each member creates OR updates one slot, writing the block's
// prescription into the selected weeks and leaving the others inactive
// (weeks[w].sets = [] at the wire level).
//
// Merge rules (the progression gesture — clone W1-W3, retarget W4-W6, Add —
// must land in ONE stored slot):
//   1. placement.slotIds[i] match → that slot (PATCH-stable identity).
//   2. else a composable slot with the same exerciseId, the same row shape
//      (count + per-row kind) and NO active-week overlap — strictly
//      complementary weeks only, so a new block can never silently swallow
//      an existing slot's prescription.
//   3. else a new slot is appended, under the recorded id when it is still
//      free, a freshly minted uuid otherwise (PE-49c). An empty
//      placement.slotIds[i] means "not bound to a slot yet"; it never becomes
//      a slot's id, so no two slots of a draft share "".
// Slot-level fields (flags, instructions, exercise) always take the incoming
// member's values — block edits act on every occurrence (§4.5).

import {
  blankCell,
  rawForReplacedText,
  type CellDraft,
  type Draft,
  type SessionDraft,
  type SetRowDraft,
  type SlotDraft,
} from "./programEdit.js";
import {
  cloneSlotDraft,
  hasActiveWeek,
  hasComposableVariants,
  type Block,
  type BlockGroup,
  type BlockMember,
  type BlockSetRow,
  type ComposedBlock,
} from "./programBlocks.js";
import { uuidv4 } from "./uuid.js";

// One block row → one matrix cell. Both expansion paths (write back into
// an existing slot, and build a fresh slot) land here, so the time axis
// can never survive on one path and be dropped on the other.
const cellFromBlockRow = (br: BlockSetRow): CellDraft => {
  const cell: CellDraft = { reps: br.reps, intensityValue: br.intensityValue };
  if (br.amrap) cell.amrap = true;
  if (br.workDurationSec !== undefined)
    cell.workDurationSec = br.workDurationSec;
  if (br.restSec !== undefined) cell.restSec = br.restSec;
  if (br.repsMin !== undefined) cell.repsMin = br.repsMin;
  if (br.repsMax !== undefined) cell.repsMax = br.repsMax;
  return cell;
};

// Same set count AND same per-row intensity kind: the block's rows can be
// written per-week into the existing matrix without disturbing other weeks.
//
// ⚠️ workKind belongs in this test for a reason the intensity kind makes
// easy to miss: it lives on the ROW, shared by every week. Merging a reps
// block into the complementary weeks of a timed slot would therefore
// rewrite the shape of the weeks it never touched — the W1-W3 bouts would
// quietly become reps sets. Different shape ⇒ a new slot, not a merge.
//
// #336: a row also takes a kind one of its rounds already holds (kg W1-3,
// RIR W4), so editing that round keeps the slot. A kind it has nowhere is
// still a new slot.
const sameRowShape = (slot: SlotDraft, member: BlockMember): boolean =>
  slot.setRows.length === member.setRows.length &&
  slot.setRows.every((r, i) => {
    const kind = member.setRows[i]!.intensityKind;
    return (
      (r.intensityKind === kind || r.cells.some((c) => c.kind === kind)) &&
      (r.workKind ?? "") === (member.setRows[i]!.workKind ?? "")
    );
  });

const coversAllActive = (slot: SlotDraft, weeks: number[]): boolean =>
  slot.inactiveWeeks.every((off, w) => off || weeks.includes(w));

const slotFields = (member: BlockMember) => ({
  exerciseId: member.exerciseId,
  exerciseName: member.exerciseName,
  instructions: member.instructions,
  amrapLastSet: member.amrapLastSet,
  maxAttempt: member.maxAttempt,
  rpeEnabled: member.rpeEnabled,
  rirEnabled: member.rirEnabled,
});

// Write the member's prescription into the block's weeks of an existing,
// shape-compatible slot. Other weeks' cells stay untouched.
const writeWeeks = (
  slot: SlotDraft,
  member: BlockMember,
  weeks: number[],
): SlotDraft => ({
  ...slot,
  ...slotFields(member),
  // PE-21 — the block REPLACES this slot's cue text. Any translation
  // behind the old text describes the old text, so it is dropped unless
  // the write leaves the string exactly as it was. See rawForReplacedText.
  instructionsRaw: rawForReplacedText(
    slot.instructions,
    member.instructions,
    slot.instructionsRaw,
  ),
  setRows: slot.setRows.map((row, r): SetRowDraft => {
    const br = member.setRows[r]!;
    const cells = row.cells.map((c) => ({ ...c }));
    const notesPerCell = [...row.notesPerCell];
    const notesRawPerCell = row.notesRawPerCell
      ? [...row.notesRawPerCell]
      : undefined;
    for (const w of weeks) {
      const cell = cellFromBlockRow(br);
      // Not the row's axis: the round keeps its own (#336).
      if (br.intensityKind !== row.intensityKind) cell.kind = br.intensityKind;
      cells[w] = cell;
      if (notesRawPerCell)
        notesRawPerCell[w] = rawForReplacedText(
          notesPerCell[w] ?? "",
          br.note,
          notesRawPerCell[w],
        );
      notesPerCell[w] = br.note;
    }
    const next: SetRowDraft = {
      ...row,
      cells,
      notesPerCell,
      ...(notesRawPerCell ? { notesRawPerCell } : {}),
    };
    // The BLOCK's shape wins over the slot's: spreading `row` would keep
    // a stale workKind after the author turned a timed set back into a
    // reps set, and the row would then emit a bout nobody prescribed.
    if (br.workKind) next.workKind = br.workKind;
    else delete next.workKind;
    return next;
  }),
  inactiveWeeks: slot.inactiveWeeks.map((off, w) =>
    weeks.includes(w) ? false : off,
  ),
});

// Build a fresh slot for the member: block weeks carry the prescription,
// the rest are inactive placeholders. variantsRaw stays empty — buildPayload
// synthesises the single every-week variant, which is exactly what a
// composable slot stores.
const slotFromMember = (
  member: BlockMember,
  weeks: number[],
  cycleWeeks: number,
  id: string,
  group: BlockGroup | null,
): SlotDraft => ({
  id,
  ...slotFields(member),
  setRows: member.setRows.map((br) => ({
    intensityKind: br.intensityKind,
    ...(br.workKind ? { workKind: br.workKind } : {}),
    cells: Array.from({ length: cycleWeeks }, (_, w) =>
      weeks.includes(w) ? cellFromBlockRow(br) : blankCell(),
    ),
    notesPerCell: Array.from({ length: cycleWeeks }, (_, w) =>
      weeks.includes(w) ? br.note : "",
    ),

  })),
  group,
  variantsRaw: [],
  inactiveWeeks: Array.from({ length: cycleWeeks }, (_, w) => !weeks.includes(w)),
});

const overlapsActive = (slot: SlotDraft, weeks: number[]): boolean =>
  weeks.some((w) => !slot.inactiveWeeks[w]);

// The one slot scan behind both lookups below: a recorded id wins outright,
// otherwise match on shape and let the caller's `weeksMatch` decide which
// side of the overlap question it wants. The two callers differ ONLY in that
// predicate, so they must not drift on the rest of the match.
const findSlotIdx = (
  slots: SlotDraft[],
  slotId: string,
  member: BlockMember,
  weeksMatch: (slot: SlotDraft) => boolean,
): number => {
  if (slotId) {
    const byId = slots.findIndex((s) => s.id === slotId);
    if (byId >= 0) return byId;
  }
  return slots.findIndex(
    (s) =>
      hasComposableVariants(s) &&
      s.exerciseId === member.exerciseId &&
      sameRowShape(s, member) &&
      weeksMatch(s),
  );
};

// Merge target for applyBlock. Without a recorded id, only the strictly
// complementary case merges (the progression gesture: clone W1-W3 → retarget
// W4-W6). Overlapping weeks append a NEW slot instead — silently overwriting
// another slot's content would be data loss; the P5 validator surfaces the
// duplicate.
const mergeTargetIdx = (
  slots: SlotDraft[],
  slotId: string,
  member: BlockMember,
  weeks: number[],
): number =>
  findSlotIdx(slots, slotId, member, (s) => !overlapsActive(s, weeks));

// Locate target for remove/update: the slot that actually RUNS the block's
// weeks (the inverse selectivity of mergeTargetIdx).
const locateIdx = (
  slots: SlotDraft[],
  slotId: string,
  member: BlockMember,
  weeks: number[],
): number =>
  findSlotIdx(slots, slotId, member, (s) => overlapsActive(s, weeks));

// Deterministic fresh group id for a new multi-member block: b1, b2, …
// skipping ids already present in the session.
const freshGroupId = (slots: SlotDraft[]): string => {
  const taken = new Set(slots.map((s) => s.group?.id).filter(Boolean));
  let n = 1;
  while (taken.has(`b${n}`)) n++;
  return `b${n}`;
};

const mapSession = (
  draft: Draft,
  dayIdx: number,
  fn: (s: SessionDraft) => SessionDraft,
): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) => (i === dayIdx ? fn(s) : s)),
});

// Expand one block into the draft (the composer's ＋ AGGIUNGI). New slots
// append — order within a day is add-order (§4.6); expandBlocks restores
// recorded positions for the re-edit path.
export const applyBlock = (draft: Draft, block: Block): Draft => {
  if (block.kind === "verbatim") {
    // cloneBlock empties the id for a fresh identity; the slot gets its own
    // here, at birth, like every other slot applyBlock creates (PE-49c).
    const slot = cloneSlotDraft(block.slot);
    return mapSession(draft, block.dayIdx, (s) => ({
      ...s,
      slots: [...s.slots, { ...slot, id: slot.id || uuidv4() }],
    }));
  }
  let next = draft;
  for (const placement of block.placements) {
    if (!next.sessions[placement.dayIdx]) continue;
    next = mapSession(next, placement.dayIdx, (session) => {
      const slots = [...session.slots];
      // Multi-member blocks always (re)assert their grouping; single-member
      // blocks only write a group the placement recorded (derived from
      // storage), never invent one.
      const group =
        placement.group ??
        (block.members.length >= 2
          ? { id: freshGroupId(slots), kind: "superset" as const }
          : null);
      block.members.forEach((member, i) => {
        const idx = mergeTargetIdx(
          slots,
          placement.slotIds[i] ?? "",
          member,
          block.weeks,
        );
        const target = idx >= 0 ? slots[idx]! : null;
        if (target) {
          const updated = sameRowShape(target, member)
            ? writeWeeks(target, member, block.weeks)
            : coversAllActive(target, block.weeks)
              ? slotFromMember(
                  member,
                  block.weeks,
                  draft.cycleWeeks,
                  target.id,
                  target.group,
                )
              : null;
          if (updated) {
            slots[idx] = group ? { ...updated, group } : updated;
            return;
          }
        }
        // Fall-through create. Carry the recorded slot id only when no slot
        // in the session still holds it (an incompatible by-id target above
        // stays alive — a duplicated id would corrupt the PATCH).
        // Otherwise mint one (PE-49c): two exercises added in the same edit
        // used to share "", so nothing downstream could tell them apart. The
        // server keeps a client id at save (`slot.id || randomUUID()`).
        const carriedId = placement.slotIds[i] ?? "";
        const idFree = carriedId !== "" && !slots.some((s) => s.id === carriedId);
        slots.push(
          slotFromMember(
            member,
            block.weeks,
            draft.cycleWeeks,
            idFree ? carriedId : uuidv4(),
            group,
          ),
        );
      });
      return { ...session, slots };
    });
  }
  return next;
};

// Deactivate the block's weeks on every occurrence, keeping the (possibly
// now fully-inactive) slots in place. updateBlock relies on the kept slots:
// applyBlock then rewrites them by id at their original index instead of
// re-appending, so an edit never reorders the session.
const clearBlockWeeks = (draft: Draft, block: ComposedBlock): Draft => {
  let next = draft;
  for (const placement of block.placements) {
    if (!next.sessions[placement.dayIdx]) continue;
    next = mapSession(next, placement.dayIdx, (session) => {
      const slots = [...session.slots];
      block.members.forEach((member, i) => {
        const idx = locateIdx(
          slots,
          placement.slotIds[i] ?? "",
          member,
          block.weeks,
        );
        if (idx < 0) return;
        const slot = slots[idx]!;
        slots[idx] = {
          ...slot,
          inactiveWeeks: slot.inactiveWeeks.map(
            (off, w) => off || block.weeks.includes(w),
          ),
        };
      });
      return { ...session, slots };
    });
  }
  return next;
};

// Drop slots left with no active week, but only in the touched sessions —
// elsewhere an all-inactive slot is pre-existing user state, not ours to GC.
const sweepSessions = (draft: Draft, days: Set<number>): Draft => ({
  ...draft,
  sessions: draft.sessions.map((s, i) =>
    days.has(i)
      ? { ...s, slots: s.slots.filter(hasActiveWeek) }
      : s,
  ),
});

// ✕ Rimuovi: deactivate the block's weeks on every occurrence; a slot left
// with no active week is removed from its session entirely.
export const removeBlock = (draft: Draft, block: Block): Draft => {
  if (block.kind === "verbatim") {
    return mapSession(draft, block.dayIdx, (s) => ({
      ...s,
      slots: block.slot.id
        ? s.slots.filter((sl) => sl.id !== block.slot.id)
        : s.slots.filter((_, i) => i !== block.position),
    }));
  }
  return sweepSessions(
    clearBlockWeeks(draft, block),
    new Set(block.placements.map((p) => p.dayIdx)),
  );
};

// Composer pre-load for ⧉ Clona: same content, fresh identity (no slot ids,
// no positions, no group — expansion synthesises a new grouping if needed).
// applyBlock mints the new slots' ids.
export const cloneBlock = (block: Block): Block =>
  block.kind === "verbatim"
    ? { ...block, slot: { ...cloneSlotDraft(block.slot), id: "", group: null } }
    : {
        kind: "composed",
        members: block.members.map((m) => ({
          ...m,
          setRows: m.setRows.map((r) => ({ ...r })),
        })),
        weeks: [...block.weeks],
        placements: block.placements.map((p) => ({
          dayIdx: p.dayIdx,
          slotIds: p.slotIds.map(() => ""),
          positions: p.positions.map(() => -1),
          group: null,
        })),
      };

// ✎ Edit's Aggiorna: clear the old occurrences' weeks (slots stay in place),
// expand the new block — by-id targets rewrite at their original index, so
// the edit never reorders the session — then sweep slots left fully inactive
// (e.g. days the edit dropped). Slot ids/positions carry over for days the
// block keeps so the PATCH updates slots instead of delete+create. Members
// reconcile by exerciseId first, then by index (covers "Cambia esercizio").
export const updateBlock = (
  draft: Draft,
  oldBlock: Block,
  newBlock: Block,
): Draft => {
  if (oldBlock.kind !== "composed" || newBlock.kind !== "composed") {
    return applyBlock(removeBlock(draft, oldBlock), newBlock);
  }
  const reconciled: ComposedBlock = {
    ...newBlock,
    placements: newBlock.placements.map((p) => {
      const old = oldBlock.placements.find((op) => op.dayIdx === p.dayIdx);
      if (!old) return p;
      const used = new Set<number>();
      const mapping = newBlock.members.map((m, i) => {
        let j = oldBlock.members.findIndex(
          (om, jj) => !used.has(jj) && om.exerciseId === m.exerciseId,
        );
        if (j < 0 && !used.has(i) && i < oldBlock.members.length) j = i;
        if (j >= 0) used.add(j);
        return j;
      });
      return {
        ...p,
        slotIds: mapping.map((j, i) =>
          j >= 0 ? (old.slotIds[j] ?? "") : (p.slotIds[i] ?? ""),
        ),
        positions: mapping.map((j, i) =>
          j >= 0 ? (old.positions[j] ?? -1) : (p.positions[i] ?? -1),
        ),
        group: p.group ?? old.group,
      };
    }),
  };
  const touched = new Set([
    ...oldBlock.placements.map((p) => p.dayIdx),
    ...reconciled.placements.map((p) => p.dayIdx),
  ]);
  return sweepSessions(
    applyBlock(clearBlockWeeks(draft, oldBlock), reconciled),
    touched,
  );
};

// Rebuild a full Draft from a session skeleton (ids/names/notes kept, slots
// discarded) plus a block list, then restore each session's original slot
// order from the recorded positions. Blocks without positions (fresh adds)
// keep their relative apply order at the end. This is the §3.2 identity
// path: expandBlocks(draft, deriveBlocks(draft)) → buildPayload ≡ draft's.
export const expandBlocks = (skeleton: Draft, blocks: Block[]): Draft => {
  let next: Draft = {
    ...skeleton,
    sessions: skeleton.sessions.map((s) => ({ ...s, slots: [] })),
  };
  for (const b of blocks) next = applyBlock(next, b);

  const posBySession = new Map<number, Map<string, number>>();
  const record = (day: number, id: string, pos: number) => {
    if (!id || pos < 0) return;
    let m = posBySession.get(day);
    if (!m) {
      m = new Map();
      posBySession.set(day, m);
    }
    const prev = m.get(id);
    if (prev == null || pos < prev) m.set(id, pos);
  };
  for (const b of blocks) {
    if (b.kind === "verbatim") record(b.dayIdx, b.slot.id, b.position);
    else
      for (const p of b.placements)
        p.slotIds.forEach((id, i) => record(p.dayIdx, id, p.positions[i] ?? -1));
  }
  return {
    ...next,
    sessions: next.sessions.map((s, day) => {
      const m = posBySession.get(day);
      if (!m || s.slots.length < 2) return s;
      const keyed = s.slots.map((sl, i) => ({
        sl,
        k: m.get(sl.id) ?? Number.MAX_SAFE_INTEGER,
        i,
      }));
      keyed.sort((a, b) => a.k - b.k || a.i - b.i);
      return { ...s, slots: keyed.map((x) => x.sl) };
    }),
  };
};
